// =============================================================================
// DASHBOARD COMMERCIAL - Tableau de bord Principal V2
// =============================================================================
// KPIs, évolution mensuelle, répartition par statut, départements
// Utilise UNIQUEMENT les variables CSS globales (--oh-*) pour le thème

import { useState, useEffect, useMemo } from "react";
import { useStatutsConfig } from "../../hooks/useGovernance";
import { useAuth } from "../../contexts/AuthContext";
import { YearSelector, useYearFilter } from "../../components/ui/year-selector";
import {
  LayoutDashboard,
  FolderKanban,
  Users,
  TrendingUp,
  TrendingDown,
  Clock,
  AlertCircle,
  CheckCircle2,
  ArrowRight,
  Building2,
  Thermometer,
  FileText,
  Calendar,
  RefreshCw,
  Receipt,
  MapPin,
  BarChart3,
  Minus,
  UserCheck,
  Handshake,
  PenTool,
  ClipboardCheck,
  Crown,
  Hammer,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, AreaChart, Area, PieChart, Pie, Legend,
} from "recharts";
import { enteteTenant } from "../../utils/enteteTenant";
import { nombreFR } from "../../utils/formatNombre";

const API = process.env.REACT_APP_BACKEND_URL;


// Couleurs pour les statuts dans les graphiques
const STATUT_COLORS = [
  "var(--oh-stat-blue-text)", "#8b5cf6", "var(--oh-stat-cyan-text)", "#10b981", "var(--oh-stat-amber-text)",
  "var(--oh-stat-red-text)", "#ec4899", "#14b8a6", "#6366f1", "#84cc16",
  "var(--oh-stat-orange-text)", "#0ea5e9", "var(--oh-stat-purple-text)", "var(--oh-stat-green-text)",
];

// Couleurs pour les départements
const DEPT_COLORS = {
  "75": "var(--oh-stat-blue-text)", "77": "#8b5cf6", "78": "var(--oh-stat-cyan-text)",
  "91": "#10b981", "92": "var(--oh-stat-amber-text)", "93": "var(--oh-stat-red-text)",
  "94": "#ec4899", "95": "#14b8a6",
};

// Tooltip personnalisé
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-lg px-3 py-2 text-xs shadow-xl border"
      style={{
        background: "var(--oh-bg-elevated)",
        borderColor: "var(--oh-border)",
        color: "var(--oh-text)",
      }}
    >
      <p className="font-medium mb-1" style={{ color: "var(--oh-text)" }}>{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color }}>
          {entry.name}: <span className="font-medium">{entry.value}</span>
        </p>
      ))}
    </div>
  );
};

export default function MetierDashboard({ tenantId, onNavigate }) {
  const { user: currentUser } = useAuth();
  // OH-0019 : "installateur" mirrored sur "external" (restrictions dashboard identiques).
  const isExternal = currentUser?.role === "external" || currentUser?.role === "installateur";
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const { config: statutsConfig } = useStatutsConfig();
  const statutLabels = useMemo(() => {
    const all = {};
    for (const codes of Object.values(statutsConfig?.statuts || {})) {
      for (const [code, info] of Object.entries(codes)) {
        if (!all[code]) all[code] = info.label;
      }
    }
    return all;
  }, [statutsConfig]);
  const [recentDossiers, setRecentDossiers] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

  useEffect(() => {
    loadDashboard();
  }, [tenantId]);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const headers = {
        ...enteteTenant(tenantId),
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const [dashRes, dossiersRes] = await Promise.all([
        fetch(`${API}/api/metier/stats/dashboard-commercial`, { headers, credentials: "include" }),
        fetch(`${API}/api/metier/dossiers?limit=5`, { headers, credentials: "include" }),
      ]);

      if (dashRes.ok) setData(await dashRes.json());
      if (dossiersRes.ok) setRecentDossiers(await dossiersRes.json());
    } catch (err) {
      console.error("Erreur dashboard:", err);
    } finally {
      setLoading(false);
    }
  };

  // Préparer les données d'évolution pour n'afficher que les 12 derniers mois
  const evolutionData = useMemo(() => {
    if (!data?.evolution_mensuelle) return [];
    return data.evolution_mensuelle
      .filter(m => m.mois.startsWith(selectedYear))
      .map((m) => ({
        ...m,
        label: new Date(m.mois + "-01").toLocaleDateString("fr-FR", { month: "short", year: "2-digit" }),
      }));
  }, [data, selectedYear]);

  // Années disponibles
  const availableYears = useYearFilter(data);

  // Préparer les données par statut
  const statutData = useMemo(() => {
    if (!data?.par_statut) return [];
    // Ordre métier des statuts
    const ORDER = [
      "prospect", "dossier_cree", "devis_signe",
      "chantier_a_planifier", "chantier_planifie", "chantier_en_cours", "chantier_termine",
      "conformite_interne_en_cours", "dossier_depose",
      "aides_en_instruction", "aides_validees",
      "demande_avance", "avance_payee", "demande_solde",
      "facture_envoyee", "paye", "archive", "refuse_anah",
    ];
    const map = {};
    data.par_statut.forEach(s => { map[s.statut] = s; });
    return ORDER
      .filter(k => map[k])
      .map(k => ({ ...map[k], label: statutLabels[k] || k }));
  }, [data]);

  // Préparer les données par département
  const deptData = useMemo(() => {
    if (!data?.par_departement) return [];
    return data.par_departement.map((d) => ({
      ...d,
      label: `Dép. ${d.departement}`,
      fill: DEPT_COLORS[d.departement] || "#6366f1",
    }));
  }, [data]);

  // Données pour le graphique pipeline AMO
  const commercial = data?.commercial || {};
  const pipelineAmo = useMemo(() => {
    if (!commercial.par_statut_amo) return [];
    const colors = { "Visite AMO": "#8b5cf6", "attente devis": "var(--oh-stat-amber-text)", "attente validation client": "var(--oh-stat-cyan-text)", "Dépôt Dossier": "#10b981", "Abandonné": "var(--oh-stat-red-text)" };
    return commercial.par_statut_amo.map(s => ({
      statut: s.statut,
      count: s.count,
      fill: colors[s.statut] || "#6366f1",
    }));
  }, [commercial.par_statut_amo]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin" style={{ color: "var(--oh-text-muted)" }} />
      </div>
    );
  }

  const totaux = data?.totaux || {};
  const ceMois = data?.ce_mois || {};

  return (
    <div className="space-y-6" data-testid="dashboard-commercial">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1
            className="text-2xl font-medium flex items-center gap-3"
            style={{ color: "var(--oh-text)" }}
          >
            <LayoutDashboard className="w-7 h-7 text-[var(--oh-info-text)]" />
            Dashboard Commercial
          </h1>
          <p className="text-sm mt-1" style={{ color: "var(--oh-text-muted)" }}>
            Vue d'ensemble de l'activité
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={loadDashboard}
          data-testid="refresh-dashboard-btn"
          style={{ borderColor: "var(--oh-border)", color: "var(--oh-text-secondary)" }}
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* KPIs — adaptés selon rôle */}
      <div className={`grid grid-cols-2 md:grid-cols-3 ${isExternal ? "lg:grid-cols-4" : "lg:grid-cols-5"} gap-4`} data-testid="kpi-grid">
        {isExternal ? (
          <>
            <KPICard title="Mes dossiers" value={totaux.dossiers || 0} icon={FolderKanban} accent="var(--oh-stat-blue-text)"
              subtitle={`${ceMois.dossiers || 0} ce mois`} variation={ceMois.variation} />
            <KPICard title="Devis en cours" value={commercial.devis_en_cours || 0} icon={PenTool} accent="var(--oh-stat-amber-text)"
              subtitle={`${commercial.contrats_signes || 0} signés`} />
            <KPICard title="Travaux validés" value={data?.travaux_valides || commercial.contrats_signes || 0} icon={Hammer} accent="#10b981"
              subtitle="Aides acceptées" />
            <KPICard title="Montant total" value={`${nombreFR(((totaux.montant_travaux || 0) / 1000), 0)}k€`} icon={TrendingUp} accent="#8b5cf6"
              subtitle="Volume travaux" />
          </>
        ) : (
          <>
            <KPICard title="Dossiers Actifs" value={totaux.dossiers || 0} icon={FolderKanban} accent="var(--oh-stat-blue-text)"
              subtitle={`${ceMois.dossiers || 0} ce mois`} variation={ceMois.variation} />
            <KPICard title="Contrats signés" value={commercial.contrats_signes || 0} icon={PenTool} accent="#10b981"
              subtitle={`${totaux.dossiers ? Math.round((commercial.contrats_signes || 0) / totaux.dossiers * 100) : 0}% des dossiers`} />
            <KPICard title="Mandats signés" value={commercial.mandats_signes || 0} icon={Handshake} accent="#8b5cf6"
              subtitle={`${totaux.dossiers ? Math.round((commercial.mandats_signes || 0) / totaux.dossiers * 100) : 0}% des dossiers`} />
            <KPICard title="Taux conversion" value={`${totaux.dossiers ? Math.round((commercial.contrats_signes || 0) / totaux.dossiers * 100) : 0}%`} icon={TrendingUp} accent="var(--oh-stat-amber-text)"
              subtitle="Prospect → Contrat" />
            <KPICard title="Clients" value={totaux.clients || 0} icon={Users} accent="#ec4899"
              subtitle="Bénéficiaires" />
          </>
        )}
      </div>

      {/* Graphiques : Evolution + Statuts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Evolution mensuelle */}
        <div
          className="lg:col-span-2 rounded-xl p-5"
          style={{
            background: "var(--oh-bg-elevated)",
            border: "1px solid var(--oh-border)",
          }}
          data-testid="chart-evolution"
        >
          <h3
            className="font-medium mb-4 flex items-center gap-2"
            style={{ color: "var(--oh-text)" }}
          >
            <BarChart3 className="w-4 h-4 text-[var(--oh-info-text)]" />
            Évolution mensuelle
            <div className="ml-auto flex gap-1">
              <YearSelector
                selectedYear={selectedYear}
                onYearChange={setSelectedYear}
                availableYears={availableYears}
                accentColor="var(--oh-stat-blue-text)"
              />
            </div>
          </h3>
          {evolutionData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={evolutionData}>
                <defs>
                  <linearGradient id="gradDossiers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--oh-stat-blue-text)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="var(--oh-stat-blue-text)" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradVisites" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--oh-stat-amber-text)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="var(--oh-stat-amber-text)" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradContrats" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--oh-border)" />
                <XAxis dataKey="label" tick={{ fill: "var(--oh-text-muted)", fontSize: 11 }} />
                <YAxis tick={{ fill: "var(--oh-text-muted)", fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  wrapperStyle={{ fontSize: 12, color: "var(--oh-text-secondary)" }}
                />
                <Area
                  type="monotone"
                  dataKey="dossiers"
                  name="Dossiers créés"
                  stroke="var(--oh-stat-blue-text)"
                  fill="url(#gradDossiers)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="visites"
                  name="Visites"
                  stroke="var(--oh-stat-amber-text)"
                  fill="url(#gradVisites)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="contrats_signes"
                  name="Contrats signés"
                  stroke="#10b981"
                  fill="url(#gradContrats)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-48" style={{ color: "var(--oh-text-muted)" }}>
              Pas de données d'évolution
            </div>
          )}
        </div>

        {/* Répartition par statut */}
        <div
          className="rounded-xl p-5"
          style={{
            background: "var(--oh-bg-elevated)",
            border: "1px solid var(--oh-border)",
          }}
          data-testid="chart-statuts"
        >
          <h3
            className="font-medium mb-4 flex items-center gap-2"
            style={{ color: "var(--oh-text)" }}
          >
            <FolderKanban className="w-4 h-4 text-[var(--oh-accent-text)]" />
            Répartition par statut
          </h3>
          {statutData.length > 0 ? (
            <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
              {statutData.map((s, i) => {
                const maxCount = Math.max(...statutData.map((x) => x.count));
                const pct = maxCount > 0 ? (s.count / maxCount) * 100 : 0;
                const color = STATUT_COLORS[i % STATUT_COLORS.length];
                return (
                  <div key={s.statut} className="flex items-center gap-2 text-xs">
                    <span
                      className="w-24 truncate text-right"
                      style={{ color: "var(--oh-text-secondary)" }}
                    >
                      {s.label}
                    </span>
                    <div className="flex-1 h-5 rounded-full overflow-hidden" style={{ background: "var(--oh-bg-surface)" }}>
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${pct}%`, backgroundColor: color }}
                      />
                    </div>
                    <span className="w-8 text-right font-mono font-medium" style={{ color: "var(--oh-text)" }}>
                      {s.count}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center h-48" style={{ color: "var(--oh-text-muted)" }}>
              Aucun statut
            </div>
          )}
        </div>
      </div>

      {/* Pipeline AMO + Mandataire + Départements */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pipeline Statut AMO */}
        <div
          className="rounded-xl p-5"
          style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}
          data-testid="pipeline-amo"
        >
          <h3 className="font-medium mb-4 flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
            <Crown className="w-4 h-4 text-[var(--oh-info-text)]" />
            Pipeline Commercial
          </h3>
          {pipelineAmo.length > 0 ? (
            <div className="space-y-2.5">
              {pipelineAmo.map((s) => {
                const maxCount = Math.max(...pipelineAmo.map(x => x.count));
                const pct = maxCount > 0 ? (s.count / maxCount) * 100 : 0;
                return (
                  <div key={s.statut} className="flex items-center gap-2 text-xs">
                    <span className="w-28 truncate text-right" style={{ color: "var(--oh-text-secondary)" }}>
                      {s.statut}
                    </span>
                    <div className="flex-1 h-5 rounded-full overflow-hidden" style={{ background: "var(--oh-bg-surface)" }}>
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: s.fill }} />
                    </div>
                    <span className="w-8 text-right font-mono font-medium" style={{ color: "var(--oh-text)" }}>
                      {s.count}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8" style={{ color: "var(--oh-text-muted)" }}>Aucune donnée pipeline</div>
          )}
        </div>

        {/* Mandataire */}
        <div
          className="rounded-xl p-5"
          style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}
          data-testid="par-mandataire"
        >
          <h3 className="font-medium mb-4 flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
            <Handshake className="w-4 h-4 text-[var(--oh-accent-text)]" />
            Mandataire
          </h3>
          {(commercial.par_mandataire || []).length > 0 ? (
            <div className="space-y-2">
              {commercial.par_mandataire.map((m) => {
                const labels = { tef: "TEF", installateur: "Installateur", autre: "Autre", aucun: "Aucun (client)" };
                const colors = { tef: "var(--oh-stat-blue-text)", installateur: "var(--oh-stat-amber-text)", autre: "#6366f1", aucun: "var(--oh-stat-slate-text)" };
                return (
                  <div key={m.mandataire} className="flex items-center justify-between p-2.5 rounded-lg"
                    style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)" }}>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: colors[m.mandataire] || "#6366f1" }} />
                      <span className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>
                        {labels[m.mandataire] || m.mandataire}
                      </span>
                    </div>
                    <span className="text-sm font-medium" style={{ color: colors[m.mandataire] || "#6366f1" }}>
                      {m.count}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-4" style={{ color: "var(--oh-text-muted)" }}>Aucune donnée</div>
          )}
        </div>

        {/* Départements */}
        <div
          className="rounded-xl p-5"
          style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}
          data-testid="chart-departements"
        >
          <h3 className="font-medium mb-4 flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
            <MapPin className="w-4 h-4 text-[var(--oh-success-text)]" />
            Par département
          </h3>
          {deptData.length > 0 ? (
            <div className="space-y-2">
              {deptData.map((d) => (
                <div key={d.departement} className="flex items-center gap-3">
                  <span className="w-12 text-xs font-mono font-medium px-2 py-0.5 rounded text-center"
                    style={{ backgroundColor: d.fill + "20", color: d.fill }}>
                    {d.departement}
                  </span>
                  <div className="flex-1 h-3 rounded-full overflow-hidden" style={{ background: "var(--oh-bg-surface)" }}>
                    <div className="h-full rounded-full"
                      style={{ width: `${(d.count / Math.max(...deptData.map(x => x.count))) * 100}%`, backgroundColor: d.fill }} />
                  </div>
                  <span className="text-xs font-medium w-6 text-right" style={{ color: "var(--oh-text)" }}>
                    {d.count}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4" style={{ color: "var(--oh-text-muted)" }}>Aucun code postal</div>
          )}
        </div>
      </div>

      {/* Dossiers Récents */}
      <div className="grid grid-cols-1 gap-6">
        <div
          className="rounded-xl p-5"
          style={{
            background: "var(--oh-bg-elevated)",
            border: "1px solid var(--oh-border)",
          }}
          data-testid="recent-dossiers"
        >
          <h3
            className="font-medium mb-4 flex items-center gap-2"
            style={{ color: "var(--oh-text)" }}
          >
            <FolderKanban className="w-4 h-4 text-[var(--oh-accent-text)]" />
            Dossiers Récents
          </h3>
          {recentDossiers.length === 0 ? (
            <div className="text-center py-8" style={{ color: "var(--oh-text-muted)" }}>
              <FolderKanban className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Aucun dossier pour le moment</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                style={{ borderColor: "var(--oh-border)" }}
                onClick={() => onNavigate?.("nouveau-dossier")}
              >
                Créer un dossier
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {recentDossiers.map((dossier) => {
                const label = statutLabels[dossier.statut_global || dossier.statut] || dossier.statut;
                return (
                  <button
                    key={dossier.id}
                    onClick={() => onNavigate?.("dossier", { id: dossier.id })}
                    className="flex items-center gap-4 p-3 rounded-lg w-full text-left group transition-all"
                    style={{
                      background: "var(--oh-bg-surface)",
                      border: "1px solid transparent",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "var(--oh-border)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "transparent";
                    }}
                    data-testid={`dossier-row-${dossier.id}`}
                  >
                    <div
                      className="p-2 rounded-lg"
                      style={{ background: "rgba(59,130,246,0.15)" }}
                    >
                      <Building2 className="w-4 h-4 text-[var(--oh-accent-text)]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: "var(--oh-text)" }}>
                        {dossier.client_nom || dossier.référence}
                      </p>
                      <p className="text-xs truncate" style={{ color: "var(--oh-text-muted)" }}>
                        {dossier.référence}
                      </p>
                    </div>
                    <Badge
                      className="text-xs"
                      style={{
                        background: "var(--oh-bg-surface)",
                        border: "1px solid var(--oh-border)",
                        color: "var(--oh-text-secondary)",
                      }}
                    >
                      {label}
                    </Badge>
                    <ArrowRight className="w-4 h-4 transition-colors" style={{ color: "var(--oh-text-muted)" }} />
                  </button>
                );
              })}
              <Button
                variant="ghost"
                size="sm"
                className="w-full mt-2"
                style={{ color: "var(--oh-text-muted)" }}
                onClick={() => onNavigate?.("dossiers")}
                data-testid="view-all-dossiers-btn"
              >
                Voir tous les dossiers
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// COMPOSANT KPI CARD
// =============================================================================
function KPICard({ title, value, icon: Icon, accent, subtitle, variation }) {
  return (
    <div
      className="rounded-xl p-5 transition-all"
      style={{
        background: "var(--oh-bg-elevated)",
        border: "1px solid var(--oh-border)",
      }}
      data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm mb-1" style={{ color: "var(--oh-text-muted)" }}>{title}</p>
          <p className="text-3xl font-medium" style={{ color: "var(--oh-text)" }}>{value}</p>
          {subtitle && (
            <p className="text-xs mt-1" style={{ color: "var(--oh-text-secondary)" }}>
              {subtitle}
            </p>
          )}
        </div>
        <div className="p-3 rounded-lg" style={{ backgroundColor: accent + "20" }}>
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </div>
      </div>
      {variation !== undefined && variation !== null && (
        <div className="flex items-center gap-1 mt-3 text-xs">
          {variation > 0 ? (
            <>
              <TrendingUp className="w-3 h-3 text-[var(--oh-success-text)]" />
              <span className="text-[var(--oh-success-text)]">+{variation} vs mois précédent</span>
            </>
          ) : variation < 0 ? (
            <>
              <TrendingDown className="w-3 h-3 text-[var(--oh-error-text)]" />
              <span className="text-[var(--oh-error-text)]">{variation} vs mois précédent</span>
            </>
          ) : (
            <>
              <Minus className="w-3 h-3" style={{ color: "var(--oh-text-muted)" }} />
              <span style={{ color: "var(--oh-text-muted)" }}>Stable</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
